import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatDate } from "@/lib/api";
import { GraduationCap, FileText, Tag, Truck, Lifebuoy, Megaphone, ArrowRight, CheckCircle, Warning, Storefront, Envelope, Fire } from "@phosphor-icons/react";

const QUICKLINKS = [
  { to: "/dashboard/pellets", icon: Fire, label: "Pellets", desc: "Member pricing + order" },
  { to: "/training", icon: GraduationCap, label: "Training", desc: "Videos & guides" },
  { to: "/downloads", icon: FileText, label: "Downloads", desc: "Forms, templates, docs" },
  { to: "/inventory", icon: Truck, label: "Early Access", desc: "Used / demo / refurb" },
  { to: "/support", icon: Lifebuoy, label: "Support", desc: "Submit a ticket" },
  { to: "/account", icon: Megaphone, label: "Account", desc: "Membership & billing" },
];

const LEVEL2_LINKS = [
  { to: "/dashboard/listing", icon: Storefront, label: "My Listing", desc: "Public profile" },
  { to: "/dashboard/leads", icon: Envelope, label: "Lead Inbox", desc: "Customer requests" },
];

export default function Dashboard() {
  const { user, isMember } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [a, t] = await Promise.all([
          api.get("/announcements"),
          api.get("/tickets"),
        ]);
        setAnnouncements(a.data);
        setTickets(t.data.slice(0, 3));
      } catch (err) {
        console.error("Dashboard data load failed:", err);
      }
    })();
  }, []);

  const expires = user?.membership_expires_at;
  const daysLeft = expires ? Math.max(0, Math.ceil((new Date(expires) - new Date()) / (1000 * 60 * 60 * 24))) : 0;
  const isFreeAccount = !isMember && user?.role !== "admin";

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
        {isFreeAccount && (
          <div className="surface p-5 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-[#00D4FF]/40" data-testid="choose-plan-banner">
            <div>
              <div className="section-label mb-1" style={{ color: "#00D4FF" }}>— UNLOCK FULL ACCESS</div>
              <div className="font-plex text-sm text-white">
                You&apos;re on the <span className="text-[#00D4FF] font-bold">free</span> plan. Pick Level 1 or Level 2 to unlock pellet pricing, training, downloads, and (Level 2) your public listing.
              </div>
            </div>
            <Link to="/pricing" className="btn-primary text-xs py-2 px-5 whitespace-nowrap" data-testid="banner-choose-plan-cta">
              Choose a Plan
            </Link>
          </div>
        )}
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-10">
          <div className="lg:col-span-7">
            <div className="section-label mb-3">— CONTROL ROOM</div>
            <h1 className="font-chivo font-black text-4xl lg:text-5xl text-white uppercase tracking-tight" data-testid="dashboard-headline">
              Welcome back, <span className="text-[#00D4FF]">{user?.name?.split(" ")[0] || "Operator"}</span>
            </h1>
            <p className="font-plex text-[#9CA3AF] mt-3">Your private resource center is live. Pick up where you left off.</p>
          </div>

          <div className="lg:col-span-5 surface p-6" data-testid="membership-status-card">
            <div className="section-label mb-3">MEMBERSHIP</div>
            <div className="flex items-start justify-between gap-4">
              <div>
                {isMember ? (
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle size={20} weight="fill" className="text-[#00D4FF]" />
                    <span className="font-chivo font-bold text-xl text-white uppercase">Active</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 mb-2">
                    <Warning size={20} weight="fill" className="text-[#FF5722]" />
                    <span className="font-chivo font-bold text-xl text-[#FF5722] uppercase">Inactive</span>
                  </div>
                )}
                <div className="font-mono text-xs text-[#9CA3AF]">
                  {isMember ? (
                    <>
                      Renews <span className="text-white">{formatDate(expires)}</span>
                      <span className="block text-[#52525B]">{daysLeft} days remaining</span>
                    </>
                  ) : (
                    <span>Activate to unlock all features.</span>
                  )}
                </div>
              </div>
              {!isMember ? (
                <Link to="/pricing" className="btn-primary text-xs py-2 px-4" data-testid="choose-plan-btn">Choose a Plan</Link>
              ) : (
                <Link to="/account" className="btn-secondary text-xs py-2 px-4" data-testid="manage-membership-btn">Manage</Link>
              )}
            </div>
          </div>
        </div>

        {/* Quick Links Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#262626] border border-[#262626] mb-6" data-testid="quicklinks-grid">
          {QUICKLINKS.map((l) => (
            <Link key={l.to} to={l.to} className="bg-[#0A0A0A] p-6 hover:bg-[#121212] transition-colors group" data-testid={`quicklink-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <l.icon size={28} weight="duotone" className="text-[#00D4FF] mb-4" />
              <div className="font-chivo font-bold text-white uppercase tracking-tight">{l.label}</div>
              <div className="font-plex text-xs text-[#9CA3AF] mt-1">{l.desc}</div>
              <ArrowRight size={14} className="text-[#52525B] group-hover:text-[#00D4FF] mt-3" weight="bold" />
            </Link>
          ))}
        </div>

        {(user?.level || 0) >= 2 && (
          <div className="mb-10" data-testid="level2-strip">
            <div className="section-label mb-3">— NETWORK PRO TOOLS</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#262626] border border-[#262626]">
              {LEVEL2_LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="bg-[#0A0A0A] p-6 hover:bg-[#121212] transition-colors group" data-testid={`l2-quicklink-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <l.icon size={28} weight="duotone" className="text-[#00D4FF] mb-4" />
                  <div className="font-chivo font-bold text-white uppercase tracking-tight">{l.label}</div>
                  <div className="font-plex text-xs text-[#9CA3AF] mt-1">{l.desc}</div>
                  <ArrowRight size={14} className="text-[#52525B] group-hover:text-[#00D4FF] mt-3" weight="bold" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Two columns: Announcements + Tickets */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="surface p-6" data-testid="announcements-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="section-label">ANNOUNCEMENTS</div>
            </div>
            <div className="space-y-4">
              {announcements.length === 0 && <div className="font-mono text-xs text-[#52525B]">No announcements.</div>}
              {announcements.map((a) => (
                <div key={a.id} className="border-l-2 border-[#00D4FF] pl-4 py-1" data-testid={`announcement-${a.id}`}>
                  <div className="font-chivo font-bold text-white">{a.title}</div>
                  <div className="font-plex text-sm text-[#9CA3AF] mt-1">{a.body}</div>
                  <div className="font-mono text-xs text-[#52525B] mt-1">{formatDate(a.created_at)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="surface p-6" data-testid="recent-tickets-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="section-label">RECENT TICKETS</div>
              <Link to="/support" className="font-mono text-xs text-[#00D4FF] hover:underline">VIEW ALL →</Link>
            </div>
            <div className="space-y-3">
              {tickets.length === 0 && <div className="font-mono text-xs text-[#52525B]">No tickets yet. <Link to="/support" className="text-[#00D4FF]">Create one</Link>.</div>}
              {tickets.map((t) => (
                <Link to={`/support/${t.id}`} key={t.id} className="block border border-[#262626] p-4 hover:border-[#404040] transition-colors" data-testid={`ticket-row-${t.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-chivo font-bold text-white">{t.subject}</div>
                    <span className={`tag ${t.status === "open" ? "tag-active" : ""}`}>{t.status}</span>
                  </div>
                  <div className="font-mono text-xs text-[#52525B]">{t.category} · {formatDate(t.updated_at)}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
